/* Exact configuration pricing. This module never substitutes another combination. */
(function (scope) {
  'use strict';
  const same = (a, b) => a != null && b != null && String(a) === String(b);
  const questions = cfg => (cfg.stepGroups || []).flatMap(g => g.steps || []).flatMap(s => s.questions || []);
  function selectorMatches(selector, answers, cfg) {
    if (!['equal', 'equals', 'eq', undefined].includes(selector.type)) return false;
    const answer = answers[selector.question];
    if (answer == null) return false;
    const question = questions(cfg).find(q => same(q.id, selector.question));
    const selected = question?.selections?.find(s => same(s.id, answer));
    // Some older size data uses a size value in a selector and an ID in answers.
    const values = [answer, selected?.value, selected?.numericValue].filter(x => x != null && x !== '');
    return values.some(value => same(selector.value, value) || same(selector.id, value));
  }
  function resolve(cfg, answers) {
    const errors = [], lines = [];
    const qs = questions(cfg);
    for (const q of qs) {
      const value = answers[q.id];
      if (value == null || value === '') {
        if (q.required) errors.push({code: 'REQUIRED_CHOICE', question: q.id});
      } else if (!(q.selections || []).some(s => same(s.id, value))) {
        errors.push({code: 'UNKNOWN_CHOICE', question: q.id});
      }
    }
    if (!(cfg.mapGroups || []).length) errors.push({code: 'NO_PRICE_DATA'});
    for (const group of cfg.mapGroups || []) {
      const matches = (group.maps || []).filter(map => {
        if (map.status != null && ![1, '1', 'active'].includes(map.status)) return false;
        const selectors = map.selectors || [];
        // A map must account for every question declared by its group.
        if (!(group.questions || []).every(id => selectors.some(s => same(s.question, id)))) return false;
        return selectors.every(s => selectorMatches(s, answers, cfg));
      });
      if (matches.length !== 1) {
        // Optional groups still require an explicit zero-price "none" map.
        errors.push({code: matches.length ? 'AMBIGUOUS_COMBINATION' : 'UNPRICED_COMBINATION', group: group.id});
        continue;
      }
      const map = matches[0];
      const price = String(map.price ?? '');
      const amount = /^\d+(\.\d{1,2})?$/.test(price) ? Math.round(Number(price) * 100) : NaN;
      if (!Number.isSafeInteger(amount) || amount < 0) {
        errors.push({code: 'INVALID_PRICE', group: group.id}); continue;
      }
      // map.price is the complete group price, never a per-component price.
      const components = map.components || (map.products || []).map(p => ({reference: p.name || p.id, quantity: map.qty || 1}));
      if (components.some(c => !Number.isSafeInteger(Number(c.quantity)) || Number(c.quantity) < 1)) {
        errors.push({code: 'INVALID_COMPONENT_QUANTITY', group: group.id}); continue;
      }
      if (cfg.sourceVerified && amount > 0 && (!components.length || components.some(c => !c.sku))) {
        errors.push({code: 'MISSING_ORDER_SKU', group: group.id}); continue;
      }
      lines.push({group: group.id, title: group.title || 'Configuration', mapId: map.id, cents: amount, components});
    }
    const cents = lines.reduce((total, line) => total + line.cents, 0);
    if (!Number.isSafeInteger(cents) || cents <= 0) errors.push({code: 'INVALID_TOTAL'});
    return {valid: !errors.length, cents: errors.length ? null : cents, lines, errors};
  }
  function payload(cfg, answers, quantity, context) {
    const result = resolve(cfg, answers);
    if (!result.valid) throw new Error('Choose a supported configuration before adding to cart.');
    if (!Number.isSafeInteger(quantity) || quantity < 1 || quantity > 99) throw new Error('Enter a whole quantity from 1 to 99.');
    if (!/^\d+$/.test(String(context.variantId || ''))) throw new Error('The product reference is missing.');
    const properties = {};
    for (const q of questions(cfg)) {
      const selected = (q.selections || []).find(s => same(s.id, answers[q.id]));
      if (selected) properties[q.label || q.title || q.id] = selected.label;
    }
    properties._config_id = context.configId;
    properties._configuration_version = cfg.version || 'legacy-exact-20260911';
    properties._configuration_components = JSON.stringify(result.lines.flatMap(line => line.components));
    properties._configuration_maps = JSON.stringify(result.lines.map(line => line.mapId));
    return {...(cfg.sourceVerified ? {configuration: {version: cfg.version, answers: {...answers}}} : {}), product_title: context.productTitle, variant_id: String(context.variantId), quantity,
      calculated_price: result.cents, properties,
      customer_note: `Firepit configuration — ${Object.entries(properties).filter(([k]) => !k.startsWith('_')).map(([k,v]) => `${k}: ${v}`).join(' | ')}`};
  }
  scope.EF_FIREPIT_PRICING = Object.freeze({resolve, payload, selectorMatches, questions});
})(typeof window === 'undefined' ? globalThis : window);
